import { useState, useEffect, useCallback } from 'react'
import { Table, Group, TextInput, Select, Title, Pagination, Badge, ActionIcon, Modal, Button, Stack, Switch, Card, Text, Avatar } from '@mantine/core'
import { IconSearch, IconEdit, IconTrash } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api } from '@/lib/api'
import { formatName } from '@/lib/format'

const roleOptions = [
  { value: 'all', label: 'Бүгд' },
  { value: 'student', label: 'Суралцагч' },
  { value: 'member', label: 'Гишүүн' },
  { value: 'teacher', label: 'Багш' },
  { value: 'admin', label: 'Админ' },
]

const roleEditOptions = [
  { value: 'student', label: 'Суралцагч' },
  { value: 'member', label: 'Гишүүн' },
  { value: 'teacher', label: 'Багш' },
  { value: 'admin', label: 'Админ' },
]

export default function UserList() {
  const [users, setUsers] = useState<Record<string, unknown>[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string | null>('all')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editUser, setEditUser] = useState<Record<string, unknown> | null>(null)
  const [editRole, setEditRole] = useState<string | null>('student')
  const [editPremium, setEditPremium] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    if (roleFilter && roleFilter !== 'all') params.role = roleFilter
    if (search) params.search = search
    api.admin.getUsers(params).then((res) => {
      setUsers(res.data)
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
    })
  }, [pagination.page, roleFilter, search])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteUser(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Хэрэглэгч устгагдлаа', color: 'green' })
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Устгахад алдаа гарлаа', color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  const openEdit = (user: Record<string, unknown>) => {
    setEditUser(user)
    setEditRole(user.role as string)
    setEditPremium(user.isPremium as boolean || false)
  }

  const handleSave = async () => {
    if (!editUser) return
    setSaving(true)
    try {
      await api.admin.updateUser(editUser._id as string, { role: editRole, isPremium: editPremium })
      notifications.show({ title: 'Амжилттай', message: 'Хэрэглэгч шинэчлэгдлээ', color: 'green' })
      setEditUser(null)
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Алдаа гарлаа', color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Title order={2} mb="md">Хэрэглэгчид</Title>

      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Select
          data={roleOptions}
          value={roleFilter}
          onChange={(v) => { setRoleFilter(v); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ minWidth: 150 }}
        />
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={600}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хэрэглэгч</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Роль</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Премиум</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u, i) => (
                <Table.Tr key={u._id as string}>
                  <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar src={u.avatar as string} size={36} radius="xl">{formatName(u.fullName as string)?.charAt(0)}</Avatar>
                      <div>
                        <Text size="sm" fw={500}>{formatName(u.fullName as string)}</Text>
                        <Text size="xs" c="dimmed">{u.email as string}</Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={u.role === 'admin' ? 'red' : u.role === 'teacher' ? 'blue' : u.role === 'member' ? 'green' : 'gray'}>
                      {u.role as string}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={u.isPremium ? 'green' : 'gray'}>{u.isPremium ? 'Тийм' : 'Үгүй'}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(u)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(u._id as string)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {users.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text ta="center" c="dimmed" py="xl">Хэрэглэгч олдсонгүй</Text>
                  </Table.Td>

                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Card>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />

      <Modal opened={!!editUser} onClose={() => setEditUser(null)} title="Хэрэглэгч засах" centered>
        <Stack>
          <Select label="Роль" data={roleEditOptions} value={editRole} onChange={setEditRole} />
          <Switch label="Премиум" checked={editPremium} onChange={(e) => setEditPremium(e.currentTarget.checked)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditUser(null)}>Болих</Button>
            <Button onClick={handleSave} loading={saving}>Хадгалах</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  )
}
